
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
} from '@mui/material';

const EditModal = ({ open, handleClose, data, onSave }) => {
  const [formData, setFormData] = useState(data || {});
  const [customerIdExists, setCustomerIdExists] = useState(false);
  const [isCheckingCustomerId, setIsCheckingCustomerId] = useState(false);

  useEffect(() => {
    // Ensure date and time are in correct format for input fields
    const formattedData = { ...data };
    if (formattedData.Date && !formattedData.Date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Attempt to parse and format if not already in YYYY-MM-DD
      const dateObj = new Date(formattedData.Date);
      if (!isNaN(dateObj)) {
        formattedData.Date = dateObj.toISOString().slice(0, 10);
      } else {
        formattedData.Date = '';
      }
    } else if (!formattedData.Date) {
      formattedData.Date = '';
    }

    if (formattedData.Time && !formattedData.Time.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
      // Attempt to parse and format if not already in HH:mm or HH:mm:ss
      const [hours, minutes, seconds] = formattedData.Time.split(':');
      if (hours && minutes) {
        formattedData.Time = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
      } else {
        formattedData.Time = '';
      }
    } else if (!formattedData.Time) {
      formattedData.Time = '';
    }

    setFormData(formattedData || {});
  }, [data]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomerIdBlur = async () => {
    if (formData['Customer ID']) {
      setIsCheckingCustomerId(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries/check-customer-id/${formData['Customer ID']}?currentId=${data.id}`);
        const result = await response.json();
        setCustomerIdExists(result.exists);
      } catch (error) {
        console.error('Error checking customer ID:', error);
      }
      setIsCheckingCustomerId(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!data) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Entry</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="Customer Name"
                label="Customer Name"
                fullWidth
                value={formData['Customer Name'] || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Place"
                label="Place"
                fullWidth
                value={formData.Place || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Date"
                label="Date"
                type="date"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={formData.Date || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Time"
                label="Time"
                type="time"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={formData.Time || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Serial No"
                label="Serial No"
                fullWidth
                value={formData['Serial No'] || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="System Name"
                label="System Name"
                fullWidth
                value={formData['System Name'] || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Contact Number"
                label="Contact Number"
                fullWidth
                value={formData['Contact Number'] || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Payment Status"
                label="Payment Status"
                select
                fullWidth
                value={formData['Payment Status'] || ''}
                onChange={handleChange}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Dispatched">Dispatched</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="Customer ID"
                label="Customer ID"
                fullWidth
                value={formData['Customer ID'] || ''}
                onChange={handleChange}
                onBlur={handleCustomerIdBlur}
                error={customerIdExists}
                helperText={customerIdExists ? 'Customer ID already exists.' : ''}
                InputProps={{
                  endAdornment: isCheckingCustomerId && <CircularProgress size={20} />,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Address"
                label="Address"
                fullWidth
                value={formData.Address || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="Customer Issue"
                label="Customer Issue"
                fullWidth
                value={formData['Customer Issue'] || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={customerIdExists || isCheckingCustomerId}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal;
